// Everything diplomacy that must not be missed, in one place so the solo game
// and a multiplayer match mount the same four lanes: a treaty offered to you,
// a marriage offered to you, and news of your offers and of war.
//
// The lane and the answer to an offer are one thing (`TreatyDesk`): the
// player answers from here and from the Diplomacy tab, and both answers have
// to reach the same notices — so the desk is owned by whoever mounts the game
// and handed to both.
import type { GameSnapshot } from '@aod/protocol';
import type { PlayerHudEntry } from '@aod/ui';
import { offersTo, readDiplomacy } from '../dynasty/diplomacy.js';
import { proposalTo, readDynasty } from '../dynasty/dynasty.js';
import type { TreatyDesk } from '../dynasty/useTreatyNews.js';
import { useOpNews } from '../espionage/useOpNews.js';
import type { DynastyActions } from '../sim/useSimWorld/useDynastyIntents.js';
import { MarriageProposalToast } from './MarriageProposalToast.js';
import { OpNotices } from './OpNotices.js';
import { TreatyNotices } from './TreatyNotices.js';
import { TreatyOfferToast } from './TreatyOfferToast.js';

export function DiplomacyToasts({
  snapshot,
  players,
  localPlayerIndex,
  desk,
  dynasty: marriage,
}: {
  readonly snapshot: GameSnapshot | null;
  readonly players: readonly PlayerHudEntry[];
  readonly localPlayerIndex: number;
  /** The treaty lane: its notices, and the answer to an offer. */
  readonly desk: TreatyDesk;
  readonly dynasty: DynastyActions;
}) {
  const diplomacy = readDiplomacy(snapshot);
  const dynasty = readDynasty(snapshot);
  const nameOf = (seat: number) => players.find((p) => p.playerId === seat)?.name ?? `#${seat + 1}`;
  const opNews = useOpNews(snapshot, localPlayerIndex);
  const offer = offersTo(diplomacy, localPlayerIndex)[0] ?? null;
  const marriageFrom = dynasty ? (proposalTo(dynasty, localPlayerIndex)?.from ?? null) : null;
  return (
    // One stack in its own lane below the merchant offer: the cards differ in
    // height, so separate fixed lanes would land on one another.
    <div className="pointer-events-none fixed inset-x-0 top-[calc(var(--hud-panel-top)+11rem)] z-50 flex flex-col items-center gap-2 px-3">
      <MarriageProposalToast
        dynasty={dynasty}
        from={marriageFrom}
        localSeat={localPlayerIndex}
        fromName={nameOf(marriageFrom ?? 0)}
        onAccept={marriage.accept}
        onDecline={marriage.decline}
      />
      <TreatyOfferToast
        offer={offer}
        fromName={nameOf(offer?.from ?? 0)}
        onAccept={(from) => {
          desk.answer(from, true);
        }}
        onDecline={(from) => {
          desk.answer(from, false);
        }}
      />
      <TreatyNotices notices={desk.notices} nameOf={nameOf} />
      <OpNotices entries={opNews} nameOf={nameOf} />
    </div>
  );
}
